export interface Series { key: string; label: string; color: string; values: number[] }
export interface Overlay { label: string; color: string; values: number[] }

const PAD = { top: 8, right: 8, bottom: 20, left: 32 };

export default function StackedBars({
  labels, series, overlay, ariaLabel, height = 160,
}: { labels: string[]; series: Series[]; overlay?: Overlay; ariaLabel: string; height?: number }) {
  const n = labels.length;
  const width = Math.max(320, n * 14 + PAD.left + PAD.right);
  const totals = labels.map((_, i) => series.reduce((s, x) => s + (x.values[i] ?? 0), 0));
  const max = Math.max(1, ...totals);
  const plotW = width - PAD.left - PAD.right;
  const plotH = height - PAD.top - PAD.bottom;
  const barW = Math.max(2, (plotW / Math.max(1, n)) * 0.7);
  const x = (i: number) => PAD.left + (i + 0.5) * (plotW / Math.max(1, n)) - barW / 2;
  const y = (v: number) => PAD.top + plotH - (v / max) * plotH;
  const ticks = [0, max / 2, max];
  const labelAt = [0, Math.floor((n - 1) / 2), n - 1].filter((i, idx, arr) => i >= 0 && arr.indexOf(i) === idx);

  return (
    <figure className="w-full overflow-x-auto">
      <svg role="img" aria-label={ariaLabel} viewBox={`0 0 ${width} ${height}`} className="h-auto w-full min-w-[320px] text-neutral-500">
        {ticks.map((tv) => (
          <g key={tv}>
            <line x1={PAD.left} x2={width - PAD.right} y1={y(tv)} y2={y(tv)} stroke="currentColor" strokeOpacity={0.15} />
            <text x={PAD.left - 4} y={y(tv) + 3} textAnchor="end" fontSize={9} fill="currentColor">{Math.round(tv)}</text>
          </g>
        ))}
        {labels.map((label, i) => {
          let acc = 0;
          const parts = series.map((s) => {
            const v = s.values[i] ?? 0;
            const rect = <rect key={s.key} x={x(i)} y={y(acc + v)} width={barW} height={Math.max(0, y(acc) - y(acc + v))} fill={s.color} rx={1} />;
            acc += v;
            return rect;
          });
          const o = overlay?.values[i] ?? 0;
          return (
            <g key={label}>
              <title>{`${label}: ${totals[i]}${overlay ? ` (${overlay.label}: ${o})` : ''}${series.length > 1 ? '\n' + series.map((s) => `${s.label}: ${s.values[i] ?? 0}`).join('\n') : ''}`}</title>
              {parts}
              {overlay && o > 0 ? <rect x={x(i)} y={y(o) - 1} width={barW} height={2} fill={overlay.color} /> : null}
            </g>
          );
        })}
        {labelAt.map((i) => (
          <text key={i} x={x(i) + barW / 2} y={height - 6} textAnchor="middle" fontSize={9} fill="currentColor">{labels[i]?.slice(5)}</text>
        ))}
      </svg>
      <figcaption className="mt-1 flex flex-wrap gap-3 text-xs text-neutral-500">
        {series.map((s) => (
          <span key={s.key} className="inline-flex items-center gap-1"><span aria-hidden className="inline-block h-2 w-2 rounded-sm" style={{ background: s.color }} />{s.label}</span>
        ))}
        {overlay ? <span className="inline-flex items-center gap-1"><span aria-hidden className="inline-block h-0.5 w-3" style={{ background: overlay.color }} />{overlay.label}</span> : null}
      </figcaption>
    </figure>
  );
}
